import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ScrollText } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface ComplianceStatus {
  hasAttestation: boolean;
  isCurrent: boolean;
  currentVersion: string;
  attestedVersion?: string;
}

export function ComplianceAttestationBanner() {
  const { user } = useAuth();
  const [location, setLocation] = useLocation();
  // Sellers only — the status endpoint reports isCurrent:false for anyone
  // without an attestation (buyers included), so gate on canSell or this
  // would show app-wide to non-sellers.
  const canSell = Boolean((user as any)?.canSell);

  const { data: status, isLoading } = useQuery<ComplianceStatus>({
    queryKey: ["/api/seller/compliance/status"],
    queryFn: async () => {
      const res = await fetch("/api/seller/compliance/status", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch compliance status");
      return res.json();
    },
    enabled: canSell,
  });

  if (!canSell) return null;
  // Redundant while they're already filling out the attestation.
  if (location.startsWith("/seller/compliance")) return null;
  if (isLoading || !status) return null;
  if (status.isCurrent) return null;

  const isStale = status.hasAttestation && !status.isCurrent;

  return (
    <div className="container mx-auto px-4 pt-3">
      <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/5 p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-yellow-500/15">
            <ScrollText className="h-4 w-4 text-yellow-700 dark:text-yellow-400" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-foreground">
              {isStale
                ? "Re-acknowledge updated compliance terms"
                : "Finish compliance to start selling"}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {isStale
                ? "Stoop's compliance terms have been updated. Your listings stay visible, but can't be sold until you re-acknowledge them."
                : "Your listings can go live and show to neighbors, but they can't be purchased until you review the rules for what you sell and confirm you're operating in compliance."}
            </p>
            <Button
              type="button"
              size="sm"
              onClick={() => setLocation("/seller/compliance")}
              className="mt-3"
            >
              {isStale ? "Re-acknowledge" : "Complete compliance"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
